import React, { useRef, useState, useEffect } from "react";
import useAddDoctor from "./hooks/useAddDoctor";
import Modal from "../../common-components/Modal";
import { useTranslation } from 'react-i18next';
import SecondaryButton from "../../common-components/Buttons/SecondaryButton";
import TertiaryButton from "../../common-components/Buttons/TertiaryButton";
import PrimaryButton from "../../common-components/Buttons/PrimaryButton";
import FileUpload from "../../../images/file_upload.svg";
import Webcam from "react-webcam";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUser, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FaCamera } from "react-icons/fa6";
import { FaCloudUploadAlt } from "react-icons/fa";

const AddDoctor = ({ showModal, setIsDoctorModalOpen, isEditable = false, currentItem = {}, getAllUsers }) => {
  const { t } = useTranslation();
  const [doctorInfo, setDoctorInfo] = useState({
    doctorName: "",
    email: "",
    mobileNumber: "",
    registrationNo: "",
    signature: "",
    stamp: "",
    photo: "",
    age: 25,
    username: "",
    password: "",
    confirmPassword: "",
  });

  const [password, setPassword] = useState({
    password: true,
    confirmPassword: true,
  });

  const signatureRef = useRef(null);
  const stampRef = useRef(null);
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  const [isWebcamOpen, setIsWebcamOpen] = useState(false);

  useEffect(() => {
    if (isEditable && currentItem) {
      setDoctorInfo({
        doctorName: currentItem.name || "",
        email: currentItem.email || "",
        mobileNumber: currentItem.mobileNumber || "",
        registrationNo: currentItem.registrationNo || "",
        signature: currentItem.signature || "",
        stamp: currentItem.stamp || "",
        photo: currentItem.photo || "",
        age: currentItem.age || 25,
        username: currentItem.username || "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [isEditable, currentItem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDoctorInfo({
      ...doctorInfo,
      [name]: name === "age" ? Number(value) : value,
    });
  };

  const handleSignatureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDoctorInfo((prev) => ({
          ...prev,
          signature: reader.result.split(",")[1],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureClick = () => {
    if (signatureRef.current) {
      signatureRef.current.click();
    }
  };

  const handleStampClick = () => {
    if (stampRef.current) {
      stampRef.current.click();
    }
  };

  const handleStampUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDoctorInfo((prev) => ({
          ...prev,
          stamp: reader.result.split(",")[1],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDoctorInfo((prev) => ({
          ...prev,
          photo: reader.result.split(",")[1],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const toggleWebcam = () => {
    setIsWebcamOpen(!isWebcamOpen);
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setDoctorInfo((prev) => ({
        ...prev,
        photo: imageSrc.split(",")[1],
      }));
      setIsWebcamOpen(false);
    }
  };

  const isFormValid = () => {
    const { doctorName, mobileNumber, username } = doctorInfo;
    if (isEditable) {
      return doctorName && mobileNumber && username;
    }
    return doctorName && mobileNumber && username && doctorInfo.password && doctorInfo.password === doctorInfo.confirmPassword;
  };

  const onReset = () => {
    if (isEditable) {
      setDoctorInfo({
        doctorName: currentItem.name || "",
        email: currentItem.email || "",
        mobileNumber: currentItem.mobileNumber || "",
        registrationNo: currentItem.registrationNo || "",
        signature: currentItem.signature || "",
        stamp: currentItem.stamp || "",
        photo: currentItem.photo || "",
        age: currentItem.age || 25,
        username: currentItem.username || "",
        password: "",
        confirmPassword: "",
      });
    } else {
      setDoctorInfo({
        doctorName: "",
        email: "",
        mobileNumber: "",
        registrationNo: "",
        signature: "",
        stamp: "",
        photo: "",
        age: 25,
        username: "",
        password: "",
        confirmPassword: "",
      });
    }
    setIsWebcamOpen(false);
  };

  const onCloseModal = () => {
    setIsDoctorModalOpen(false);
    onReset();
    setIsWebcamOpen(false);
  };

  const { isAddDoctorLoading, onAddNewDoctor } = useAddDoctor({
    doctorInfo,
    isEditable,
    currentItem,
  });

  return (
    <Modal
      showModal={showModal}
      modalWidth="w-[80%]"
      modalHeight="h-[90%]"
      onClose={onCloseModal}
    >
      <div className="p-5 relative">
        <div className="flex mb-5 relative flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full border border-gray-300 flex items-center justify-center overflow-hidden">
            {isWebcamOpen && (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="object-cover rounded-full w-20 h-20"
              />
            )}
            {doctorInfo.photo && !isWebcamOpen && (
              <img
                src={`data:image/jpeg;base64,${doctorInfo.photo}`}
                alt="Profile Preview"
                className="w-full h-full object-cover"
              />
            )}
            {!isWebcamOpen && !doctorInfo.photo && (
              <FontAwesomeIcon icon={faCircleUser} size="5x" color="#2323" />
            )}
          </div>
        </div>

        <div className="flex w-full my-3 justify-between items-center">
          <button
            onClick={isWebcamOpen ? capture : toggleWebcam}
            className="flex active:animate-bounce gap-2 hover:border-2 w-[45%] h-9 border disabled:bg-[#F9FAFB] items-center justify-center bg-white border-[#C6C7C9] item-center rounded-lg text-sm text-[#1F2A37]"
          >
            <FaCamera />
            {isWebcamOpen ? `${t("Click Photo")}` : `${t("Open Camera")}`}
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageUpload}
            className="hidden"
          />
          <button
            onClick={handleImageClick}
            className="flex active:animate-bounce gap-2 w-[45%] hover:border-2 h-9 border disabled:bg-[#F9FAFB] items-center justify-center bg-white border-[#C6C7C9] item-center py-2 rounded-lg text-sm text-[#1F2A37]"
          >
            <FaCloudUploadAlt />
            {t("Upload Photo")}
          </button>
        </div>

        <div className="grid gap-5 grid-cols-3">
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Doctor Name")}*</div>
            <div className="flex">
              <input
                type="text"
                name="doctorName"
                value={doctorInfo.doctorName}
                onChange={handleChange}
                placeholder={t("Enter doctor name")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Age")}</div>
            <div className="flex">
              <input
                type="text"
                name="age"
                min={0}
                value={doctorInfo.age}
                onChange={handleChange}
                placeholder={t("Age")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Mobile Number")}*</div>
            <div className="flex">
              <input
                type="tel"
                name="mobileNumber"
                value={doctorInfo.mobileNumber}
                onChange={handleChange}
                placeholder={t("Enter mobile number")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Email")}</div>
            <div className="flex">
              <input
                type="email"
                name="email"
                value={doctorInfo.email}
                onChange={handleChange}
                placeholder={t("Enter email address")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Registration Number")}</div>
            <div className="flex">
              <input
                type="text"
                name="registrationNo"
                value={doctorInfo.registrationNo}
                onChange={handleChange}
                placeholder={t("Enter registration number")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
              />
            </div>
          </div>
          <div className="my-2">
            <div className="text-[#5E6066] mb-2.5">{t("Username")}*</div>
            <div className="flex">
              <input
                type="text"
                name="username"
                value={doctorInfo.username}
                onChange={handleChange}
                placeholder={t("Enter username")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
            </div>
          </div>
          <div className="my-2 relative">
            <div className="text-[#5E6066] mb-2.5">{t("Password")}*</div>
            <div className="flex">
              <input
                type={password.password ? "password" : "text"}
                name="password"
                value={doctorInfo.password}
                onChange={handleChange}
                placeholder={t("Enter password")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setPassword((prev) => ({
                    ...prev,
                    password: !prev.password,
                  }));
                }}
                className="px-2 absolute top-10 right-0"
              >
                <FontAwesomeIcon
                  color="#6B7280"
                  icon={password.password ? faEyeSlash : faEye}
                />
              </button>
            </div>
          </div>
          <div className="my-2 relative">
            <div className="text-[#5E6066] mb-2.5">{t("Confirm Password")}*</div>
            <div className="flex">
              <input
                type={password.confirmPassword ? "password" : "text"}
                name="confirmPassword"
                value={doctorInfo.confirmPassword}
                onChange={handleChange}
                placeholder={t("Confirm password")}
                className="border w-full bg-[#F9FAFB] focus:border-[#2D2E33] text-sm text-gray-800 py-2 focus:outline-none rounded-lg placeholder:text-[#D1D5DB] px-3"
                required
              />
              {doctorInfo.password !== doctorInfo.confirmPassword && doctorInfo.confirmPassword && (
                <p className="text-[#E02424] font-normal text-xs m-1">
                  Password and Confirm Password do not match
                </p>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setPassword((prev) => ({
                    ...prev,
                    confirmPassword: !prev.confirmPassword,
                  }));
                }}
                className="px-2 absolute top-10 right-0"
              >
                <FontAwesomeIcon
                  color="#6B7280"
                  icon={password.confirmPassword ? faEyeSlash : faEye}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-between w-[60%]">
          <div className="">
            {doctorInfo.signature && (
              <div className="w-full">
                <div className="flex w-full flex-shrink-0">
                  {t("Uploaded Signature")}
                </div>
                <img
                  src={`data:image/jpeg;base64,${doctorInfo.signature}`}
                  alt="signature Preview"
                  className="w-1/2 h-1/2 object-cover"
                />
              </div>
            )}
            <div className="text-[#5E6066] mb-2.5">
              {t("Upload Signature")}
            </div>
            <div className="border-dashed h-fit border gap-3 flex flex-col rounded-lg py-6 px-12 bg-[#F9FAFB] border-[#C6C7C9] justify-center items-center">
              <div className="flex gap-2 justify-center flex-col">
                <img
                  src={FileUpload}
                  alt="FileUpload"
                  className="h-12 w-12 mx-auto"
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={signatureRef}
                  onChange={handleSignatureUpload}
                  className="hidden"
                />
              </div>
              <SecondaryButton
                buttonName={t("Browse Files")}
                onClick={handleSignatureClick}
              />
            </div>
          </div>
          <div className="">
            {doctorInfo.stamp && (
              <div className="w-full">
                <div className="flex w-full flex-shrink-0">
                  {t("Uploaded Stamp")}
                </div>
                <img
                  src={`data:image/jpeg;base64,${doctorInfo.stamp}`}
                  alt="stamp Preview"
                  className="w-1/2 h-1/2 object-cover"
                />
              </div>
            )}
            <div className="text-[#5E6066] mb-2.5">{t("Upload Stamp")}</div>
            <div className="border-dashed h-fit border gap-3 flex flex-col rounded-lg py-6 px-12 bg-[#F9FAFB] border-[#C6C7C9] justify-center items-center">
              <div className="flex gap-2 justify-center flex-col">
                <img
                  src={FileUpload}
                  alt="FileUpload"
                  className="h-12 w-12 mx-auto"
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={stampRef}
                  onChange={handleStampUpload}
                  className="hidden"
                />
              </div>
              <SecondaryButton
                buttonName={t("Browse Files")}
                onClick={handleStampClick}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-5 mt-5 justify-end">
          <TertiaryButton
            buttonName={t("Reset")}
            width="w-fit"
            onClick={onReset}
          />
          <PrimaryButton
            buttonName={t(isEditable ? "Update" : "Save")}
            width="w-fit"
            onClick={() => {
              onAddNewDoctor()
                .then(() => {
                  getAllUsers();
                  onCloseModal();
                })
                .catch(() => {
                  // Error already handled in the hook
                });
            }}
            disabled={isAddDoctorLoading || !isFormValid()}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddDoctor;
